import { KeyboardEventCheck } from '@Tools/KeyboardEventCheck';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { getButtonState, getCurrentGameState, getGeneralAppState, getGlobalStyles, setPopupMessage } from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { EMessagesTypes } from '@UILayer/Modules/PopupMessage/PopupMessage.types';

const mainButtonsFlags = ['isSpinButtonActive', 'isStopButtonActive', 'isSkipButtonActive'];

const BigMultiButton: React.FC<{ uiFreeze: boolean }> = ({ uiFreeze }) => {
    const [state, setState] = useReducer(
        (state: { disabled: boolean; current: string; insufficientFunds: boolean }, newState: any) => ({ ...state, ...newState }),
        {
            disabled: false,
            current: '',
            insufficientFunds: false
        }
    );

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const gameStoreButtonState = useAppSelector(getButtonState);
    const gameStoreCurrentGameState = useAppSelector(getCurrentGameState);
    const gameStoreGeneralAppState = useAppSelector(getGeneralAppState);
    const globalStyles = useAppSelector(getGlobalStyles);

    const buttonCN = cn(
        globalStyles.wcaMode,
        styles.bigmulti_btn,
        styles.gameplay_btn,
        styles.gameplay_btn_type1,
        gameStoreButtonState?.isSpinButtonActive ? styles.play : null,
        gameStoreButtonState?.isContinueButtonActive ? styles.play : null,
        gameStoreButtonState?.isStopButtonActive ? styles.stop : null,
        gameStoreButtonState?.isSkipButtonActive ? styles.bigTake : null,
        globalStyles.orientation,
        globalStyles.device,
        state.disabled || uiFreeze ? styles.disabled : null
    );

    useEffect(() => {
        const states = gameStoreButtonState;
        let disabled =
            !states?.isSpinButtonActive && !states?.isStopButtonActive && !states?.isSkipButtonActive && !states?.isContinueButtonActive ? true : false;
        let currentType;

        if (uiFreeze) disabled = true;

        for (const [key, value] of Object.entries(states)) {
            if (mainButtonsFlags.includes(key)) {
                if (value) currentType = key;
            }
        }

        setState({ disabled: disabled, current: currentType });
    }, [gameStoreButtonState, uiFreeze]);

    useEffect(() => {
        const insufficientFunds = gameStoreGeneralAppState.insufficientFunds;
        const currentState = gameStoreCurrentGameState;

        setState({
            insufficientFunds: insufficientFunds && currentState === EGameStates.IDLE_STATE ? true : false
        });
    }, [gameStoreGeneralAppState, gameStoreCurrentGameState]);

    function buttonAction(e: React.MouseEvent<any>) {
        if (e)
            if (!state.disabled && !state.insufficientFunds) {
                e.stopPropagation();

                if (gameStoreButtonState?.isStopButtonActive) EventsCenter.emit(EButtonEvents.STOP_BUTTON_PRESSED);
                if (gameStoreButtonState?.isSpinButtonActive) EventsCenter.emit(EButtonEvents.SPIN_BUTTON_PRESSED);
                if (gameStoreButtonState?.isSkipButtonActive) EventsCenter.emit(EButtonEvents.SKIP_BUTTON_PRESSED);
                if (gameStoreButtonState?.isContinueButtonActive) EventsCenter.emit(EButtonEvents.CONTINUE_BUTTON_PRESSED);
            }

        if (state.insufficientFunds) {
            dispatch(setPopupMessage({ type: EMessagesTypes.insufficientFunds }));
        }
    }

    return (
        <button
            tabIndex={0}
            className={buttonCN}
            id={`ui_button_${state.current}`}
            aria-description={state.current ? t(`WCA_${state.current.toUpperCase()}_BUTTON`) : ''}
            onPointerDown={(e) => buttonAction(e)}
            onKeyDown={(e) => KeyboardEventCheck(e, buttonAction)}
        ></button>
    );
};

export default BigMultiButton;
